import {memoize, noop} from 'lodash';
import React, {Fragment, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {AsyncMoreStatusesEnum} from '../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {areUnorderedEqual} from '../../../../libs/shared/core/src/helpers/utilities/arrayHelpers';
import {isTeammateModel, TeammateModel} from '../../../../libs/shared/core/src/models/api/teammateModel';
import {isTeammateNamespace} from '../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {buildTeammateDisplayName} from '../../../../libs/shared/core/src/models/helpers/teammateModelHelpers';
import {getTeammates} from '../../../../libs/shared/core/src/store/data/teammates/commonTeammatesSelectors';
import {TeammateAvatar} from '../../../../libs/shared/ui/src/components/avatar/teammateAvatar';
import {PopoverTransferProps} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {ResourceMultiSelectFilterClearBehaviorsEnum} from '../../../components/common/filters/resourceMultiSelectFilterDropdown';
import {useAppSelector} from '../../../helpers/appReduxHelpers';
import {buildTeammateSearchableField} from '../../../helpers/modelHelpers';
import {AnalyticsFilterTypesEnum} from '../../../models/api/analyticsSavedViewModel';
import {CircleModel} from '../../../models/api/circleModel';
import {SettingsCircleTeammateSearchDropdownPicker} from '../../../settings/common/dropdowns/settingsCircleTeammateSearchDropdownPicker';
import {getCircles, getFlatCircles} from '../../../store/data/dataSelectors';
import {getTeammatesInCommonTeam, getUser} from '../../../store/data/teammates/teammatesSelectors';
import {getAnalyticsSelectedNamespace} from '../../../store/panels/analytics/parameters/analyticsPanelParametersSelectors';
import {useAnalyticsFilter} from '../../helpers/hooks/useAnalyticsFilter';
import {AnalyticsFilterButtonValue} from './common/analyticsFilterButtonValue';
import {analyticsFiltersConfig} from './common/analyticsFiltersConfig';
import {AnalyticsFilterItem, renderFilterTitle, useFiltersTracking} from './common/helpers';

/*
 * Constants.
 */

const {name, iconName, activeIconName, alternativeIconName} =
  analyticsFiltersConfig[AnalyticsFilterTypesEnum.TEAMMATES];

/*
 * Style.
 */

const StyledTeammateAvatar = styled(TeammateAvatar)`
  display: inline-block;
  vertical-align: middle;
  position: relative;
  bottom: 1px;
`;

const circlePrefix = 'cir_';
const teammatePrefix = 'tea_';

/*
 * Hook.
 */

export const useAnalyticsTeammatesFilter = (): AnalyticsFilterItem => {
  const [teammateFilter, onUpdateFilter] = useAnalyticsFilter(AnalyticsFilterTypesEnum.TEAMMATES);
  const [query, setQuery] = useState('');
  const {trackOnEdit, trackOnRemove} = useFiltersTracking(AnalyticsFilterTypesEnum.TEAMMATES);

  const allCircles = toPrefixedKeys(useAppSelector(getCircles), circlePrefix);
  // Get all teammates, included blocked ones, to always be able to show the selected values.
  const allTeammates = toPrefixedKeys(useAppSelector(getTeammates), teammatePrefix);
  const allAvailableResources = {...allCircles, ...allTeammates};

  const namespace = useAppSelector(getAnalyticsSelectedNamespace);
  const isIndividualReport = isTeammateNamespace(namespace);
  const commonTeammates = useAppSelector(getTeammatesInCommonTeam);
  const user = useAppSelector(getUser);
  const allFlatCircles = useAppSelector(getFlatCircles);
  const teammates = isIndividualReport ? [user] : [user, ...commonTeammates];

  const selectedTeammateIds = teammateFilter?.teammate_ids ?? [];
  const selectedCircleIds = teammateFilter?.circle_ids ?? [];

  const selectedPrefixedResourceIds = mergeTeammatesAndCircleIds(selectedTeammateIds, selectedCircleIds);

  const onSelectedIdsChange = (
    updatedTeammateIds: ReadonlyArray<number>,
    updatedCircleIds: ReadonlyArray<number>,
  ) => {
    if (
      areUnorderedEqual(updatedTeammateIds, selectedTeammateIds) &&
      areUnorderedEqual(updatedCircleIds, selectedCircleIds)
    ) {
      return;
    }

    onUpdateFilter({
      teammate_ids: updatedTeammateIds,
      circle_ids: updatedCircleIds,
    });

    const updatedPrefixedResourceIds = mergeTeammatesAndCircleIds(updatedTeammateIds, updatedCircleIds);

    const allResolvedTeammates = resolveTeammatesFromTeammatesAndCircles(
      updatedTeammateIds,
      updatedCircleIds,
      teammates,
    );

    trackOnEdit(updatedPrefixedResourceIds, selectedPrefixedResourceIds, {
      nb_teammates: allResolvedTeammates.size,
      nb_teammates_checked: updatedTeammateIds.length,
      nb_circles_checked: updatedCircleIds.length,
    });
  };

  const isActive = selectedCircleIds.length > 0 || selectedTeammateIds.length > 0;

  const teammateIconName = isActive ? activeIconName : iconName;

  const renderButton = () => (
    <AnalyticsFilterButtonValue
      selectedIds={selectedPrefixedResourceIds}
      itemsMap={allAvailableResources}
      renderItem={renderButtonItem}
      iconName={selectedCircleIds.length ? alternativeIconName : teammateIconName}
      label={name}
      isActive={isActive}
      shouldLabelSelection={false}
    />
  );

  const renderDropdown = (dropdownProps: PopoverTransferProps) => (
    <SettingsCircleTeammateSearchDropdownPicker
      popoverTransferProps={dropdownProps}
      query={query}
      onQueryChange={setQuery}
      renderApplyLabel={() => <Trans id="analytics-filter-teammates-apply">Apply</Trans>}
      title={<Trans id="analytics-filter-teammates-title">Teammates</Trans>}
      onAnySelectedIdsChange={onSelectedIdsChange}
      circles={{
        items: allFlatCircles.filter((circle) => circle.name.toLowerCase().includes(query.toLowerCase())),
        onSelectedIdsChange: noop,
        selectedIds: selectedCircleIds ?? [],
        moreStatus: AsyncMoreStatusesEnum.ALL_LOADED,
        onLoadMore: noop,
      }}
      teammates={{
        items: Object.values(teammates).filter((teammate) =>
          buildTeammateSearchableField(teammate).toLowerCase().includes(query.toLowerCase()),
        ),
        onSelectedIdsChange: noop,
        selectedIds: selectedTeammateIds,
        moreStatus: AsyncMoreStatusesEnum.ALL_LOADED,
        onLoadMore: noop,
      }}
      clearBehavior={ResourceMultiSelectFilterClearBehaviorsEnum.CLEAR_ALL}
      onClear={trackOnRemove}
    />
  );

  return {
    id: 'teammates-filter',
    type: AnalyticsFilterTypesEnum.TEAMMATES,
    isActive,
    title: renderFilterTitle(name, iconName),
    renderDropdown,
    renderButton,
  };
};

/*
 * Helpers.
 */

const renderButtonItem = (resource: TeammateModel | CircleModel) => {
  if (isTeammateModel(resource)) {
    return (
      <Fragment key={resource.id}>
        {resource.avatarUrl && <StyledTeammateAvatar teammate={resource} size={VisualSizesEnum.SMALL} />}{' '}
        {buildTeammateDisplayName(resource)}
      </Fragment>
    );
  }

  return <Fragment key={resource.id}>{resource.name}</Fragment>;
};

/**
 * example: {1: circle1, 2: circle2} => {'cir_1': circle1, 'cir_2': circle2}
 */
const toPrefixedKeys = memoize(<T,>(obj: Record<string, T>, prefix: string): Record<string, T> => {
  const result: Record<string, T> = {};

  for (const [key, value] of Object.entries(obj)) {
    result[`${prefix}${key}`] = value;
  }

  return result;
});

/**
 * example: [1, 2] => ['cir_1', 'cir_2']
 */
const toPrefixedIds = memoize((ids: ReadonlyArray<number> | undefined = [], prefix: string) =>
  ids.map((id) => `${prefix}${id}`),
);

/**
 * We need to prefix the selected ids to avoid conflicts between teammate and circle ids in the button text.
 * example: ['tea_1', 'tea_2', 'cir_1', 'cir_2']
 */
function mergeTeammatesAndCircleIds(
  selectedTeammateIds: ReadonlyArray<number>,
  selectedCircleIds: ReadonlyArray<number>,
) {
  return [
    ...toPrefixedIds(selectedCircleIds, circlePrefix),
    ...toPrefixedIds(selectedTeammateIds, teammatePrefix),
  ];
}

/**
 * Tracking: count the total number of teammates, even the ones included in circles.
 */
function resolveTeammatesFromTeammatesAndCircles(
  selectedTeammateIds: ReadonlyArray<number>,
  selectedCircleIds: ReadonlyArray<number>,
  allFlatTeammates: ReadonlyArray<TeammateModel>,
) {
  const allResolvedTeammates = new Set(selectedTeammateIds);

  if (selectedCircleIds.length === 0) {
    return allResolvedTeammates;
  }

  allFlatTeammates.forEach((teammate) => {
    if (teammate.circleIds.some((id) => selectedCircleIds.includes(id))) {
      allResolvedTeammates.add(teammate.id);
    }
  });

  return allResolvedTeammates;
}
